import { CONTAC_DASHBOARD } from '../views/contac/dashboard/routes';
import { RESULTADO_POR_PLACA } from '../views/contac/resultado_por_placa/routes';
import { COMISSIONAMENTOS_HOME } from '../views/comissionamentos/routes';
import { CONTROLE_PRODUCAO } from '../views/controle_producao/routes';
import { RASTREAMENTO } from '../views/rastreamento/routes';
import { ANDAMENTO_VEICULOS } from '../views/setor_eventos/routes';
import { OFICINA_EXTERNA } from '../views/oficina_externa/routes';
import { DASHBOARD_COMPRAS } from '../views/dashboard_compras/routes';
import { HOME } from '../views/home/routes';
import { TELAO } from '../views/telao/routes';
import { papeisDe, PortadorDePapeis } from './papeis';

/**
 * Para onde o usuário vai ao entrar (at-81, ADR 0044) — resolvedor ÚNICO.
 *
 * Antes desta entrega a mesma decisão vivia em QUATRO lugares (`RedirectByRole`, o handler de
 * login, o efeito de "já autenticado" da tela de login e o callback do Google), com listas
 * ligeiramente diferentes e três fontes distintas de papel. Foi assim que o `BUG-V69` nasceu:
 * uma dessas cópias apontava para uma rota que não existia.
 *
 * A ordem de precedência:
 *   1. tela inicial do GRUPO de maior prioridade (vem pronta no token);
 *   2. prioridade por PAPEL — a lista abaixo, que reproduz o comportamento anterior;
 *   3. Home.
 */

/**
 * Papéis que NÃO têm Home, em ordem de precedência.
 *
 * ⚠️ A ordem importa e não é alfabética: quem acumula funções cai no primeiro que tiver. Os
 * papéis de tela única (RASTREADOR, EVENTOS) vêm por último de propósito — se a pessoa também é
 * da Oficina, a tela dela é a da Oficina, não a de consulta.
 */
const PRIORIDADE_POR_PAPEL: Array<{ papel: string; rota: () => string }> = [
  { papel: 'COMISSIONAMENTO', rota: COMISSIONAMENTOS_HOME },
  { papel: 'RELACIONAMENTO', rota: RESULTADO_POR_PLACA },
  { papel: 'CONTAC', rota: CONTAC_DASHBOARD },
  { papel: 'CONSULTOR', rota: CONTAC_DASHBOARD },
  { papel: 'REGIONAL', rota: CONTAC_DASHBOARD },
  { papel: 'COLABORADOR', rota: CONTROLE_PRODUCAO },
  // at-75/at-85: RASTREADOR e EVENTOS não têm Home — sem estes ramos cairiam num 403 logo
  // depois do login.
  { papel: 'RASTREADOR', rota: RASTREAMENTO },
  { papel: 'EVENTOS', rota: ANDAMENTO_VEICULOS },
  // at-114: o INSPETOR também não tem Home — o ambiente dele é só a Oficina Externa. Vem por
  // último pelo mesmo motivo dos dois acima: quem acumula função na oficina interna cai na tela
  // de lá, que é onde ele opera.
  { papel: 'INSPETOR', rota: OFICINA_EXTERNA },
  // at-137: a TV da oficina abre direto no Telão em tela cheia, que é o que ela existe para mostrar.
  { papel: 'TELAO', rota: TELAO },
  // at-125: a GESTAO_COMPRAS não tem Home — sozinha, sem este ramo, cairia num 403 logo depois
  // do login. Com o COMPRA junto (o caso normal), a pessoa abre no Dashboard de Compras.
  { papel: 'GESTAO_COMPRAS', rota: DASHBOARD_COMPRAS },
];

/** Papéis cuja tela é a Home — não precisam de ramo próprio, mas documentam a intenção. */
const PAPEIS_COM_HOME = [
  'RECEPCAO',
  'CHEFE_DE_OFICINA',
  'ORCAMENTO',
  'COMPRA',
  'ESTOQUE',
  'ADMINISTRACAO',
  'ADMIN',
  'TI',
];

export function resolverRotaInicial(
  portador: (PortadorDePapeis & { telaInicial?: string | null }) | null | undefined,
): string {
  if (!portador) return HOME();

  // O grupo tem a palavra final: é a configuração explícita que alguém fez na tela de admin.
  if (portador.telaInicial) {
    return portador.telaInicial;
  }

  const papeis = papeisDe(portador);
  if (papeis.length === 0) return HOME();

  // Quem tem algum papel com Home vai para a Home — é a tela mais completa, e o resto do
  // sistema é alcançável pelo menu.
  if (papeis.some((papel) => PAPEIS_COM_HOME.includes(papel))) {
    return HOME();
  }

  const especifico = PRIORIDADE_POR_PAPEL.find(({ papel }) => papeis.includes(papel));
  return especifico ? especifico.rota() : HOME();
}
